<script setup lang="ts">
  import { computed, getCurrentInstance, onBeforeUnmount } from 'vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { useCategoryEditorContext } from '@/composition/catalog/use-category-editor';
  import { useScheduleDialog } from '@/composition/catalog/use-schedule-dialog';
  import { TCategoryWhere } from '@/domain/catalog/category-form';
  import { nextDomId } from '@/utils/dom-id.utils';
  import V2CatalogBranchesField from '../shared/V2CatalogBranchesField.vue';
  import V2CatalogEditorSection from '../shared/V2CatalogEditorSection.vue';
  import V2CatalogScheduleSummary from '../shared/V2CatalogScheduleSummary.vue';

  /**
   * «¿Dónde se ve?» of a category (plan §5.15, DESIGN §5.1): three radio
   * cards for `{ visible, posOnly }`, then «Sucursales» and «Horarios».
   */
  interface IWhereCard {
    where: TCategoryWhere;
    title: string;
    body: string;
    icon: string;
  }

  const editor = useCategoryEditorContext();
  const { data, access } = useCatalogPage();
  const { openSchedule } = useScheduleDialog();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const groupId = nextDomId('category-where');

  let unmounted = false;
  onBeforeUnmount(() => {
    unmounted = true;
  });

  const form = computed(() => editor.form.value);
  const branches = computed(() => data.activeStoreBranches.value);
  const multiBranch = computed(() => branches.value.length > 1);

  const cards = computed<IWhereCard[]>(() => [
    {
      where: 'everywhere',
      title: t('menuPage.where.everywhere'),
      body: t('menuPage.where.everywhereBody'),
      icon: 'storefront-outline',
    },
    {
      where: 'posOnly',
      title: t('menuPage.where.posOnly'),
      body: t('menuPage.where.posOnlyBody'),
      icon: 'cash-register',
    },
    {
      where: 'hidden',
      title: t('menuPage.where.hidden'),
      body: t(
        data.restaurant.value?.settings?.showDisabledItems
          ? 'menuPage.where.hiddenBodyUnavailable'
          : 'menuPage.where.hiddenBody'
      ),
      icon: 'eye-off-outline',
    },
  ]);

  /**
   * «Oculta» hides it in every branch: only roles that manage every branch
   * may change it (the API rule, §5.17), except on a new category.
   */
  const canHide = computed(
    () =>
      editor.canEditAvailability.value &&
      (access.value.canHideEverywhere || editor.isNew)
  );
  const visibleLocked = computed(
    () => editor.canEditAvailability.value && !canHide.value
  );

  /**
   * Managers choose any card. Roles that only turn things on and off move
   * between hidden and where the category was sold.
   */
  function cardEnabled(where: TCategoryWhere): boolean {
    const current = form.value;
    if (!current) return false;
    if (!editor.readOnly.value) {
      if (canHide.value) return true;
      // The stored visibility stays: a hidden category stays hidden.
      const storedHidden = (editor.base.value ?? current).where === 'hidden';
      return (where === 'hidden') === storedHidden;
    }
    if (!canHide.value) return false;
    const shownAs: TCategoryWhere = current.posOnly ? 'posOnly' : 'everywhere';
    return where === 'hidden' || where === shownAs;
  }

  function choose(where: TCategoryWhere) {
    if (!cardEnabled(where) || form.value?.where === where) return;
    editor.update('where', where);
  }

  async function editSchedule() {
    const current = form.value;
    if (!current) return;
    const result = await openSchedule({
      name: current.name.trim() || t('menuPage.editor.newCategory'),
      hourRanges: current.hourRanges,
      entity: 'category',
    });
    // The editor may have closed meanwhile.
    if (result && !unmounted) editor.update('hourRanges', result.hourRanges);
  }
</script>

<template>
  <V2CatalogEditorSection
    v-if="form"
    class="v2-catalog-category-where-section"
    :title="t('menuPage.where.title')"
  >
    <div class="where-cards" role="radiogroup" :aria-labelledby="groupId">
      <span :id="groupId" class="where-label">{{
        t('menuPage.where.title')
      }}</span>
      <button
        v-for="card in cards"
        :key="card.where"
        type="button"
        role="radio"
        class="where-card"
        :class="{ 'is-selected': form.where === card.where }"
        :aria-checked="form.where === card.where ? 'true' : 'false'"
        :aria-labelledby="`${groupId}-${card.where}`"
        :aria-describedby="`${groupId}-${card.where}-body`"
        :disabled="!cardEnabled(card.where)"
        @click="choose(card.where)"
      >
        <i
          class="mdi card-icon"
          :class="`mdi-${card.icon}`"
          aria-hidden="true"
        />
        <span class="card-text">
          <span :id="`${groupId}-${card.where}`" class="card-title">{{
            card.title
          }}</span>
          <span :id="`${groupId}-${card.where}-body`" class="card-body">{{
            card.body
          }}</span>
        </span>
        <span class="card-radio" aria-hidden="true" />
      </button>
    </div>

    <p v-if="visibleLocked" class="where-help">
      {{ t('menuPage.editor.needsAllBranches') }}
    </p>

    <V2CatalogBranchesField
      v-if="multiBranch"
      class="where-branches"
      :value="form.hiddenBranchIds"
      :branches="branches"
      :only-branch-id="access.canHideEverywhere ? null : data.branchId.value"
      :disabled="!editor.canEditAvailability.value"
      @input="editor.update('hiddenBranchIds', $event)"
    />

    <V2CatalogScheduleSummary
      class="where-schedule"
      :value="form.hourRanges"
      :can-edit="access.canSchedule"
      :upsell="access.upsellSchedule"
      :string-id="data.restaurant.value ? data.restaurant.value.stringId : null"
      @edit="editSchedule"
    />
  </V2CatalogEditorSection>
</template>

<style scoped lang="scss">
  .v2-catalog-category-where-section {
    .where-label {
      @apply tw-sr-only;
    }

    .where-help {
      @apply tw-mb-0 tw-mt-2 tw-text-sm tw-leading-relaxed tw-text-neutral-500;
    }

    .where-card {
      @apply tw-flex tw-w-full tw-cursor-pointer tw-items-start tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-p-3 tw-text-left tw-transition-colors;

      & + .where-card {
        @apply tw-mt-2;
      }

      &:hover:not(:disabled) {
        @apply tw-border-neutral-300;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      &:disabled {
        @apply tw-cursor-not-allowed tw-opacity-60;
      }

      .card-icon {
        @apply tw-mr-3 tw-flex-none tw-text-xl tw-leading-6 tw-text-neutral-500;
      }

      .card-text {
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;
      }

      .card-title {
        @apply tw-text-sm tw-font-semibold tw-leading-6 tw-text-neutral-900;
      }

      .card-body {
        @apply tw-text-sm tw-leading-snug tw-text-neutral-500;
      }

      .card-radio {
        @apply tw-ml-3 tw-mt-1 tw-h-4 tw-w-4 tw-flex-none tw-rounded-full tw-border-2 tw-border-solid tw-border-neutral-300;
      }

      &.is-selected {
        @apply tw-border-orange-600 tw-bg-orange-50;

        .card-icon {
          @apply tw-text-orange-600;
        }

        .card-radio {
          @apply tw-border-orange-600;
          box-shadow: inset 0 0 0 3px rgb(var(--c-surface)),
            inset 0 0 0 8px rgb(var(--c-orange-600));
        }
      }
    }

    .where-branches,
    .where-schedule {
      @apply tw-mt-3 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-pt-1;
    }
  }
</style>
